import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { APIComponentAttributes, Features } from '@ardoq/api-types';
import { type ReferenceComponents } from './referenceComponentsInterface';
import { toIds } from '@ardoq/core';
import { GetContextMenuOptionsArguments } from '@ardoq/context-menu';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { getTrackingFunction, TrackedContextMenus } from './tracking';
import { isTruthy } from '@ardoq/common-helpers';
import {
  getDeleteMixedEntitiesItem,
  getDropdownDivider,
  getEditPropertiesMenuItem,
  getEditStyleMenuItem,
  getReverseReferenceMenuItem,
  getToggleLockMenuItem,
} from './menuItems';
import { checkHasWriteAccess } from './utils';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { getFeatureContext } from 'streams/featureContext$';
import { getActiveScenarioState } from 'streams/activeScenario/activeScenario$';
import { activeScenarioOperations } from '@ardoq/core';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import { PermissionContext } from '@ardoq/access-control';
import { type ReferenceCreationMenuState } from 'streams/referenceCreationMenuState$';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { type I18nLocale } from '@ardoq/i18n';
import { lockOperations } from 'lockedComponents/lockOperations';

type GetMixedEntitiesMenuArgs = {
  components: APIComponentAttributes[];
  referenceComponents: ReferenceComponents;
  isViewpointMode: boolean;
  permissionContext: PermissionContext;
  workspaceAndModel: WorkspaceAndModelLookup;
  referenceCreation: ReferenceCreationMenuState;
  i18nLocale: I18nLocale;
} & GetContextMenuOptionsArguments;
export const getMixedEntitiesMenu = ({
  components,
  referenceComponents,
  target,
  isViewpointMode,
  permissionContext,
  workspaceAndModel,
  referenceCreation,
  i18nLocale,
}: GetMixedEntitiesMenuArgs): DropdownItem[] | null => {
  if (!components.length) {
    return null;
  }
  const { references, sourceComponentsById, targetComponentsById } =
    referenceComponents;
  const componentIds = toIds(components);
  const referenceIds = toIds(references);
  const workspaceIds = [
    ...new Set([
      ...components.map(c => c.rootWorkspace),
      ...references.map(r => r.rootWorkspace),
    ]),
  ];
  const hasWriteAccess = checkHasWriteAccess({
    workspaceIds,
    permissionContext,
    components,
    references,
    referenceSourceComponentsById: sourceComponentsById,
  });

  if (!hasWriteAccess) {
    // no context menu for multiselect with no write access
    return null;
  }

  const featureContext = getFeatureContext();
  const hasLockedReferences = lockOperations.isAnyLocked(
    references.map(r => r.lock),
    featureContext
  );

  const hasLockedComponents = lockOperations.isAnyLocked(
    components.map(c => c.lock),
    featureContext
  );

  const hasLockedEntities = hasLockedComponents || hasLockedReferences;
  const activeScenarioState = getActiveScenarioState();
  const canEditScenario =
    !activeScenarioOperations.isInScenarioMode(activeScenarioState) ||
    scenarioAccessControlInterface.canEditActiveScenario(
      permissionContext,
      activeScenarioState
    );
  const canEditAllReferencesTargets =
    canEditScenario &&
    references.every(reference => {
      const targetComponent = targetComponentsById[reference.target];
      if (!targetComponent) {
        return false;
      }
      return referenceAccessControlOperation.canEditReferencesTargetComponent({
        reference,
        components: [targetComponent],
        permissionContext,
        featureContext,
      });
    });

  const menuItemMixedTrackingFunction = getTrackingFunction(
    target,
    TrackedContextMenus.COMPONENTS_AND_REFERENCES_CONTEXT_MENU
  );

  const deleteMixedEntitiesItem = getDeleteMixedEntitiesItem(
    componentIds,
    referenceIds,
    menuItemMixedTrackingFunction,
    i18nLocale,
    isViewpointMode
  );

  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);
  if (isViewpointMode && isCanvasPrototype) {
    return [deleteMixedEntitiesItem];
  }

  const menu = [
    ...(hasLockedEntities
      ? []
      : [
          getEditPropertiesMenuItem(
            componentIds,
            referenceIds,
            menuItemMixedTrackingFunction,
            i18nLocale,
            isViewpointMode
          ),
          !isViewpointMode &&
            getEditStyleMenuItem(
              componentIds,
              referenceIds,
              menuItemMixedTrackingFunction,
              i18nLocale,
              isViewpointMode
            ),
        ]),

    ...(hasLockedReferences || isViewpointMode
      ? []
      : [
          getReverseReferenceMenuItem({
            referenceComponents,
            workspaceAndModel,
            referenceCreation,
            trackingFn: menuItemMixedTrackingFunction,
            canEditAllReferencesTargets,
            i18nLocale,
          }),
          !hasLockedEntities && deleteMixedEntitiesItem,
          getDropdownDivider(),
        ]),

    isViewpointMode
      ? null
      : getToggleLockMenuItem({
          components,
          references,
          trackingFn: menuItemMixedTrackingFunction,
          i18nLocale,
          permissionContext,
        }),
  ].filter(isTruthy);

  return menu.length ? menu : null;
};
